import React, { useState, useEffect } from 'react';
import {
  FiUsers,
  FiUserPlus,
  FiTrendingUp,
  FiActivity,
  FiDollarSign,
  FiFileText,
  FiArrowUp,
  FiArrowDown,
  FiMail,
  FiArrowRight,
  FiBarChart2,
  FiRefreshCw,
  FiPlus,
  FiX,
  FiShield
} from 'react-icons/fi';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent } from '@/components/mira';
import { Line, Bar } from 'react-chartjs-2';
import { debugPlatformAuth } from '../../utils/debug-platform-auth';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import apiClient from '../../services/apiClient';
import Loader from 'wrapper/loader';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const { toast } = useToast();

  const [metrics, setMetrics] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    activeLoans: 0,
    loanVolume: 0,
    pendingInvitations: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    company_name: '',
    company_nmls_id: '',  // Company NMLS ID
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    subdomain: '',
    country: 'US',
    licensed_states: [],
    region: 'US',
    expires_in_days: 30
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    // Debug authentication in development
    if (import.meta.env.DEV) {
      debugPlatformAuth();
    }

    // Load Satoshi font for platform pages
    const style = document.createElement('style');
    style.innerHTML = `
      @font-face {
        font-family: 'Satoshi';
        src: url('/fonts/Satoshi-Regular.otf') format('opentype');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Satoshi';
        src: url('/fonts/Satoshi-Medium.otf') format('opentype');
        font-weight: 500;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Satoshi';
        src: url('/fonts/Satoshi-Bold.otf') format('opentype');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Satoshi';
        src: url('/fonts/Satoshi-Black.otf') format('opentype');
        font-weight: 900;
        font-style: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);

    fetchDashboardData();

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleInviteCompany = async () => {
    if (!inviteForm.company_name || !inviteForm.company_nmls_id || !inviteForm.admin_email ||
      !inviteForm.admin_first_name || !inviteForm.admin_last_name || !inviteForm.subdomain) {
      toast({ title: 'Please fill in all required fields', variant: "destructive" });
      return;
    }

    setInviteLoading(true);
    try {
      // Step 1: Create the organization
      const orgResponse = await apiClient.post('/platform/clients/organizations', {
        name: inviteForm.company_name,
        company_nmls_id: inviteForm.company_nmls_id,  // Include company NMLS ID
        subdomain: inviteForm.subdomain,
        country: 'US',
        region: 'US',
        licensed_states: inviteForm.licensed_states,
        admin_email: inviteForm.admin_email,
        admin_first_name: inviteForm.admin_first_name,
        admin_last_name: inviteForm.admin_last_name
      });

      // Backend returns OrganizationResponse directly in data
      // The backend automatically sends an invitation during organization creation,
      // so we don't need a separate invitation API call
      if (orgResponse.data && orgResponse.data.organization_id) {
        toast({ title: `Organization "${orgResponse.data.name}" created and invitation sent to ${inviteForm.admin_email}` });
        setShowInviteModal(false);
        setInviteForm({
          company_name: '',
          company_nmls_id: '',  // Reset company NMLS ID
          admin_email: '',
          admin_first_name: '',
          admin_last_name: '',
          subdomain: '',
          country: 'US',
          licensed_states: [],
          region: 'US',
          expires_in_days: 30
        });
        fetchDashboardData(); // Refresh the dashboard
      }
    } catch (error) {
      console.error('Invitation error:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to create organization or send invitation';
      toast({ title: errorMessage, variant: "destructive" });
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // For demo, always use mock data until backend is ready
      // Uncomment below when backend endpoints are ready
      // const response = await apiClient.get('/platform/metrics');
      // setMetrics(response.data.metrics);
      // setRecentActivity(response.data.activity);

      // Use mock data for demo
      setMetrics({
        totalCompanies: 12,
        activeCompanies: 10,
        totalUsers: 487,
        activeLoans: 234,
        loanVolume: 45678900,
        pendingInvitations: 5
      });
      setRecentActivity([
        { id: 1, type: 'company_joined', company: 'First National Bank', time: '2 hours ago' },
        { id: 2, type: 'invitation_sent', company: 'Community Credit Union', time: '5 hours ago' },
        { id: 3, type: 'user_registered', user: 'John Doe', company: 'ABC Mortgage', time: '1 day ago' },
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 hover:shadow-md transition-all duration-250">
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs sm:text-sm font-semibold text-neutral600 uppercase tracking-wider">
            {title}
          </p>
          <div className="p-2 bg-primary50 rounded-lg">
            <Icon className="w-4 h-4 text-primary500" />
          </div>
        </div>
        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-secondary900">
          {value}
        </p>
        {change && (
          <div className={`flex items-center mt-2 text-xs sm:text-sm ${changeType === 'up' ? 'text-success200' : 'text-error200'}`}>
            {changeType === 'up' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
            <span className="ml-1 font-medium">
              {change}% from last month
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // Chart data
  const growthChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Companies',
        data: [5, 6, 8, 9, 10, 12],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Users',
        data: [150, 200, 280, 350, 420, 487],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const loanVolumeData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Loan Volume ($M)',
        data: [8.5, 10.2, 12.8, 14.2],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: "'Satoshi', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        bodyFont: {
          family: "'Satoshi', sans-serif"
        },
        titleFont: {
          family: "'Satoshi', sans-serif"
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            family: "'Satoshi', sans-serif",
            size: 11
          }
        }
      },
      x: {
        ticks: {
          font: {
            family: "'Satoshi', sans-serif",
            size: 11
          }
        }
      }
    },
  };

  if (loading) {
    return <Loader message="Loading dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-neutral50">
      {/* Page Header - Responsive padding and text sizes */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-secondary900">
              Platform Dashboard
            </h1>
            <p className="text-sm sm:text-base text-neutral600 mt-1">
              Monitor and manage your platform operations
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary500 text-white rounded-lg hover:bg-primary600 hover:shadow-md transition-all duration-250 touch-target"
            >
              <FiPlus className="w-4 h-4" />
              <span className="text-sm font-medium">Invite Company</span>
            </button>
            <button
              onClick={fetchDashboardData}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-secondary200 text-neutral700 rounded-lg hover:bg-neutral100 transition-all duration-250 touch-target"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Grid - Responsive with better breakpoints */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <StatCard
          title="Total Companies"
          value={metrics.totalCompanies}
          icon={FiUsers}
          change={20}
          changeType="up"
        />
        <StatCard
          title="Active Companies"
          value={metrics.activeCompanies}
          icon={FiActivity}
          change={10}
          changeType="up"
        />
        <StatCard
          title="Total Users"
          value={metrics.totalUsers.toLocaleString()}
          icon={FiUserPlus}
          change={15}
          changeType="up"
        />
        <StatCard
          title="Active Loans"
          value={metrics.activeLoans}
          icon={FiFileText}
          change={5}
          changeType="down"
        />
        <StatCard
          title="Loan Volume"
          value={`$${(metrics.loanVolume / 1000000).toFixed(1)}M`}
          icon={FiDollarSign}
          change={25}
          changeType="up"
        />
        <StatCard
          title="Pending Invites"
          value={metrics.pendingInvitations}
          icon={FiTrendingUp}
        />
      </div>

      {/* Charts Row - Responsive gaps and padding */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Growth Chart */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-secondary900 mb-3 sm:mb-4">
            Platform Growth Trends
          </h3>
          <div style={{ height: '300px' }}>
            <Line data={growthChartData} options={chartOptions} />
          </div>
        </div>

        {/* Loan Volume Chart */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-secondary900 mb-3 sm:mb-4">
            Weekly Loan Volume
          </h3>
          <div style={{ height: '300px' }}>
            <Bar data={loanVolumeData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Recent Activity & Quick Actions - Stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral200">
            <h3 className="text-base sm:text-lg font-semibold text-secondary900">
              Recent Activity
            </h3>
          </div>
          <div className="p-4 sm:p-5 md:p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-7 w-7 rounded-full bg-primary50 flex items-center justify-center">
                      {activity.type === 'company_joined' && <FiUsers className="w-3.5 h-3.5 text-primary" />}
                      {activity.type === 'invitation_sent' && <FiMail className="w-3.5 h-3.5 text-primary" />}
                      {activity.type === 'user_registered' && <FiUserPlus className="w-3.5 h-3.5 text-primary" />}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-secondary700 font-medium">
                      {activity.type === 'company_joined' && `${activity.company} joined the platform`}
                      {activity.type === 'invitation_sent' && `Invitation sent to ${activity.company}`}
                      {activity.type === 'user_registered' && `${activity.user} registered from ${activity.company}`}
                    </p>
                    <p className="text-xs text-neutral600 mt-0.5">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral200">
            <h3 className="text-base sm:text-lg font-semibold text-secondary900">
              Quick Actions
            </h3>
          </div>
          <div className="p-4 sm:p-5 md:p-6">
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/platform/invitations'}
                className="w-full flex items-center justify-between p-3 sm:p-3.5 bg-primary50 hover:bg-primary100 rounded-lg transition-all duration-250 group touch-target"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 bg-white rounded-md">
                    <FiUserPlus className="w-4 h-4 text-primary500" />
                  </div>
                  <span className="text-sm font-medium text-secondary700">
                    Invite New Company
                  </span>
                </div>
                <FiArrowRight className="w-4 h-4 text-neutral400 group-hover:text-primary500 transition-colors" />
              </button>
              <button
                onClick={() => window.location.href = '/platform/companies'}
                className="w-full flex items-center justify-between p-3 sm:p-3.5 bg-neutralGrey hover:bg-neutral100 rounded-lg transition-all duration-250 group touch-target"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 bg-white rounded-md">
                    <FiUsers className="w-4 h-4 text-neutral600" />
                  </div>
                  <span className="text-sm font-medium text-secondary700">
                    Manage Companies
                  </span>
                </div>
                <FiArrowRight className="w-4 h-4 text-neutral400 group-hover:text-neutral600 transition-colors" />
              </button>
              <button
                onClick={() => window.location.href = '/platform/analytics'}
                className="w-full flex items-center justify-between p-3 sm:p-3.5 bg-neutralGrey hover:bg-neutral100 rounded-lg transition-all duration-250 group touch-target"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 bg-white rounded-md">
                    <FiBarChart2 className="w-4 h-4 text-neutral600" />
                  </div>
                  <span className="text-sm font-medium text-secondary700">
                    View Analytics
                  </span>
                </div>
                <FiArrowRight className="w-4 h-4 text-neutral400 group-hover:text-neutral600 transition-colors" />
              </button>
              <button
                onClick={() => window.location.href = '/platform/nmls-verification'}
                className="w-full flex items-center justify-between p-3 sm:p-3.5 bg-warning_light hover:bg-warning100 rounded-lg transition-all duration-250 group touch-target"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 bg-white rounded-md">
                    <FiShield className="w-4 h-4 text-warning200" />
                  </div>
                  <span className="text-sm font-medium text-secondary700">
                    NMLS Verification
                  </span>
                </div>
                <FiArrowRight className="w-4 h-4 text-neutral400 group-hover:text-warning200 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Company Modal - Responsive sizing */}
      {showInviteModal && (
        <Dialog
          show={showInviteModal}
          onOpenChange={(open) => {
            if (!open) setShowInviteModal(false);
          }}
          showClose={false}
        >
          <DialogContent
            showClose={false}
            className="w-full max-w-md rounded-2xl bg-white p-4 sm:p-6 modal-responsive"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-secondary900">
                Invite New Company
              </h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 hover:bg-neutral100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-neutral600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={inviteForm.company_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary500 focus:ring-offset-2 transition-all duration-250"
                  placeholder="ABC Mortgage Co."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Subdomain *
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={inviteForm.subdomain}
                    onChange={(e) => setInviteForm({ ...inviteForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    className="flex-1 px-3 py-2 border border-neutral200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                    placeholder="abcmortgage"
                  />
                  <span className="px-3 py-2 bg-neutral100 border border-l-0 border-neutral200 rounded-r-lg text-sm text-neutral600">
                    .{import.meta.env.VITE_APP_BASE_DOMAIN || 'miralabs.ai'}
                  </span>
                </div>
                {/* TODO: PRODUCTION - Add subdomain availability check
                    - Real-time validation against reserved subdomains
                    - Check availability in database
                    - Show green checkmark if available
                    - Show error message if taken or reserved
                */}
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Country
                </label>
                <input
                  type="text"
                  value="United States (US)"
                  readOnly
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg bg-neutral100 text-neutral600"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Company NMLS ID *
                </label>
                <input
                  type="text"
                  value={inviteForm.company_nmls_id}
                  onChange={(e) => setInviteForm({ ...inviteForm, company_nmls_id: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                  placeholder="12345"
                />
                <p className="text-xs text-neutral500 mt-1">
                  The company's NMLS license number (required for US mortgage companies)
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Licensed States
                </label>
                <input
                  type="text"
                  value={inviteForm.licensed_states.join(', ')}
                  onChange={(e) => {
                    const states = e.target.value
                      .split(',')
                      .map(s => s.trim().toUpperCase())
                      .filter(s => s.length > 0);
                    setInviteForm({ ...inviteForm, licensed_states: states });
                  }}
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                  placeholder="CA, TX, FL"
                />
                <p className="text-xs text-neutral500 mt-1">
                  Comma-separated list of US state codes where licensed
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                    Admin First Name *
                  </label>
                  <input
                    type="text"
                    value={inviteForm.admin_first_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, admin_first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                    Admin Last Name *
                  </label>
                  <input
                    type="text"
                    value={inviteForm.admin_last_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, admin_last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Admin Email *
                </label>
                <input
                  type="email"
                  value={inviteForm.admin_email}
                  onChange={(e) => setInviteForm({ ...inviteForm, admin_email: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                  placeholder="admin@abcmortgage.com"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral700 mb-1 tracking-wide">
                  Invitation Expires In
                </label>
                <select
                  value={inviteForm.expires_in_days}
                  onChange={(e) => setInviteForm({ ...inviteForm, expires_in_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-neutral200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary100"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 px-4 py-2 border border-neutral200 text-neutral700 rounded-lg hover:bg-neutral100 transition-all duration-250 touch-target"
                disabled={inviteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteCompany}
                className="flex-1 px-4 py-2 bg-primary500 text-white rounded-lg hover:bg-primary600 hover:shadow-md transition-all duration-250 disabled:opacity-50 touch-target"
                disabled={inviteLoading}
              >
                {inviteLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Dashboard;
